import { ChevronLeft, ChevronRight, X } from "lucide-react";
import React from "react";
import type { ChatClient } from "../lib/chatApi";
import type { Product } from "../lib/mockData";
import type { OnAddToCartResponse, RecommendedProduct } from "../lib/types";
import { ProductOptionGroup } from "./ProductOptionGroup";
import { resolvePickedVariantSelection, sanitizeHtml, mapRecommendedToProduct } from "../lib/utils";
import { useLocale } from "../context/LocaleContext";
import { EVENT_RECOMMENDATIONS_UPDATED, events } from "../embed/events";
import { RecommendationsWidget } from "../embed/pages/RecommendationsWidget";

interface ProductPageOverlayProps {
  product: Product;
  open: boolean;
  onClose: () => void;
  storeId: string;
  client: ChatClient;
}

type StoredRecommendation = {
  id: string | number;
  title: string;
  price: string;
  compareAtPrice?: string;
  currencyDisplay?: string;
  images: string[];
  productUrl?: string;
  variantDimensions?: string[];
  variantCombinations?: Array<{
    values: string[];
    sku?: string;
    url?: string;
    price?: number;
    compare_at_price?: number;
    image_ids?: string[];
  }>;
  pickedVariantValues?: string[];
  imageMeta?: Array<{ id: string; url: string }>;
};

function normalizeRecommendations(products: RecommendedProduct[] | null | undefined): StoredRecommendation[] {
  if (!Array.isArray(products)) return [];
  return products.map((rp) => {
    const p = mapRecommendedToProduct(rp);
    return {
      id: p.id,
      title: p.title,
      price: p.price || "",
      compareAtPrice: p.compareAtPrice,
      currencyDisplay: p.currencyDisplay,
      images: p.images,
      productUrl: p.productUrl,
      variantDimensions: p.variantDimensions,
      variantCombinations: p.variantCombinations,
      pickedVariantValues: p.pickedVariantValues,
      imageMeta: p.imageMeta,
    } satisfies StoredRecommendation;
  });
}

function storageKey(storeId: string) {
  return `ww:rec:${storeId}`;
}

export function ProductPageOverlay({ product, open, onClose, storeId, client }: ProductPageOverlayProps) {
  const { formatMoney } = useLocale();
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastCount, setLastCount] = React.useState<number | null>(null);

  const emitUpdated = React.useCallback(() => {
    events.emit(EVENT_RECOMMENDATIONS_UPDATED);
  }, []);

  const clearStoredRecommendations = React.useCallback(() => {
    try {
      localStorage.removeItem(storageKey(storeId));
    } catch {}
    emitUpdated();
    setLastCount(null);
    setError(null);
  }, [emitUpdated, storeId]);

  const handleClose = React.useCallback(() => {
    clearStoredRecommendations();
    onClose();
  }, [clearStoredRecommendations, onClose]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true } as any);
  }, [handleClose, open]);

  const {
    title,
    price,
    compareAtPrice,
    description,
    images,
    imageMeta,
    variantDimensions,
    variantCombinations,
    pickedVariantValues,
  } = product;

  const dims = React.useMemo(
    () => (Array.isArray(variantDimensions) ? variantDimensions.slice(0, 3) : []),
    [variantDimensions]
  );
  const combos = React.useMemo(
    () =>
      Array.isArray(variantCombinations)
        ? variantCombinations.filter((c) => Array.isArray(c?.values) && c.values.length === dims.length)
        : [],
    [variantCombinations, dims.length]
  );

  const getOptionsForIndex = React.useCallback(
    (index: number, prefix: string[]): string[] => {
      const set = new Set<string>();
      for (const c of combos) {
        let ok = true;
        for (let i = 0; i < prefix.length; i++) {
          if (c.values[i] !== prefix[i]) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;
        set.add(c.values[index]);
      }
      return Array.from(set);
    },
    [combos]
  );

  const findExact = React.useCallback(
    (values: string[]) => combos.find((c) => c.values.every((v: string, i: number) => v === values[i])),
    [combos]
  );

  const deriveCascade = React.useCallback((): string[] => {
    const selection: string[] = [];
    for (let i = 0; i < dims.length; i++) {
      const opts = getOptionsForIndex(i, selection);
      selection[i] = opts[0] ?? "";
    }
    return selection;
  }, [dims.length, getOptionsForIndex]);

  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [displayPrice, setDisplayPrice] = React.useState<string>(price);
  const [displayCompareAt, setDisplayCompareAt] = React.useState<string | undefined>(compareAtPrice);

  React.useEffect(() => {
    if (dims.length === 0 || combos.length === 0) {
      setSelectedValues([]);
      setDisplayPrice(price);
      setDisplayCompareAt(compareAtPrice);
      return;
    }

    const resolvedPicked = resolvePickedVariantSelection(dims, combos, pickedVariantValues);
    const target = resolvedPicked ?? deriveCascade();
    setSelectedValues(target);
    const combo = findExact(target);
    setDisplayPrice(formatMoney(combo?.price) || price);
    setDisplayCompareAt(formatMoney((combo as any)?.compare_at_price) || undefined);
  }, [compareAtPrice, combos, deriveCascade, dims, findExact, formatMoney, pickedVariantValues, price]);

  const handleChangeAt = React.useCallback(
    (index: number, value: string) => {
      setSelectedValues((prev) => {
        const next = prev.slice(0, index);
        next[index] = value;
        for (let j = index + 1; j < dims.length; j++) {
          const opts = getOptionsForIndex(j, next);
          next[j] = opts[0] ?? "";
        }
        const combo = findExact(next);
        setDisplayPrice(formatMoney(combo?.price) || price);
        setDisplayCompareAt(formatMoney((combo as any)?.compare_at_price) || undefined);
        return next;
      });
    },
    [dims.length, findExact, formatMoney, getOptionsForIndex, price]
  );

  const normalizeKey = React.useCallback((name: string): string => {
    const lowered = name.trim().toLowerCase();
    const noDiacritics = lowered.normalize("NFD").replace(/\p{Diacritic}/gu, "");
    return noDiacritics.replace(/\s+/g, "_");
  }, []);

  const buildSelectedVariantMap = React.useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    for (let i = 0; i < dims.length; i++) {
      const name = dims[i];
      const key = normalizeKey(name);
      const val = selectedValues[i];
      if (key && typeof val === "string") map[key] = val;
    }
    return map;
  }, [dims, normalizeKey, selectedValues]);

  const selectedCombo = React.useMemo(() => {
    if (dims.length === 0 || combos.length === 0) return undefined;
    if (selectedValues.length !== dims.length) return undefined;
    return findExact(selectedValues);
  }, [dims.length, combos.length, selectedValues, findExact]);

  const visibleImages = React.useMemo(() => {
    const imageIdsRaw = (selectedCombo as any)?.image_ids;
    const imageIds = Array.isArray(imageIdsRaw)
      ? imageIdsRaw.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
      : [];

    if (imageIds.length === 0) return images;
    if (!Array.isArray(imageMeta) || imageMeta.length === 0) return images;

    const idSet = new Set(imageIds);
    const filtered = imageMeta.filter((img) => idSet.has(img.id)).map((img) => img.url);
    return filtered.length > 0 ? filtered : images;
  }, [images, imageMeta, selectedCombo]);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [imageLoads, setImageLoads] = React.useState(0);

  React.useEffect(() => {
    setImageLoads(0);
    setCanScrollLeft(false);
    setCanScrollRight(false);
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [visibleImages]);

  React.useEffect(() => {
    const checkScrollLimits = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };
    checkScrollLimits();
    const currentRef = scrollRef.current;
    currentRef?.addEventListener("scroll", checkScrollLimits, { passive: true });
    return () => currentRef?.removeEventListener("scroll", checkScrollLimits);
  }, [visibleImages]);

  React.useEffect(() => {
    const checkScrollLimits = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };
    checkScrollLimits();
  }, [imageLoads]);

  const scrollToDirection = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const scrollAmount = direction === "left" ? -clientWidth : clientWidth;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleFetchRecommendations = React.useCallback(async () => {
    if (!product) return;
    setBusy(true);
    setError(null);
    try {
      const payload: any = { product_ids: [String(product.id)] };
      if (selectedCombo?.sku) payload.selected_variant_ids = { [String(product.id)]: String(selectedCombo.sku) };

      const res: OnAddToCartResponse = await client.postOnAddToCart(payload);
      const recommended = (res as any)?.recommendation?.recommended_products as RecommendedProduct[] | undefined;
      const stored = normalizeRecommendations(recommended);
      try {
        localStorage.setItem(storageKey(storeId), JSON.stringify(stored));
      } catch {}
      emitUpdated();
      setLastCount(stored.length);
      if (stored.length === 0) {
        setError("No recommendations returned (or missing images).");
      }
    } catch (e) {
      setError((e as Error)?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }, [client, emitUpdated, product, selectedCombo?.sku, storeId]);

  if (!open || !product) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-white overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-4 right-4 z-10 w-[36px] h-[36px] flex items-center justify-center rounded-full border bg-white"
          onClick={handleClose}
          aria-label="Close product page"
        >
          <X className="w-[18px] h-[18px]" />
        </button>

        <div className="max-w-[1000px] mx-auto px-6 py-8">
          <div className="w-full relative bg-gray-50 overflow-hidden rounded-xl" style={{ paddingBottom: "75%" }}>
            <div
              ref={scrollRef}
              className="absolute inset-0 flex flex-row overflow-x-auto snap-x snap-mandatory"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <style>{`
                div[ref="scrollRef"]::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {visibleImages.length === 0 ? (
                <div className="flex items-center justify-center h-full w-full text-gray-500">No images</div>
              ) : (
                visibleImages.map((src: string, index: number) => (
                  <img
                    key={index}
                    src={src}
                    alt={`${title} - image ${index + 1}`}
                    className="h-full w-full basis-full flex-shrink-0 object-contain snap-start"
                    loading={index === 0 ? "eager" : "lazy"}
                    onLoad={() => setImageLoads((prev) => prev + 1)}
                  />
                ))
              )}
            </div>

            {visibleImages.length > 1 && (
              <>
                <button
                  onClick={() => scrollToDirection("left")}
                  disabled={!canScrollLeft}
                  aria-label="Previous image"
                  className={`absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full transition-colors ${
                    !canScrollLeft ? "opacity-50 cursor-not-allowed" : "hover:bg-white"
                  }`}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={() => scrollToDirection("right")}
                  disabled={!canScrollRight}
                  aria-label="Next image"
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full transition-colors ${
                    !canScrollRight ? "opacity-50 cursor-not-allowed" : "hover:bg-white"
                  }`}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>

          <div className="mt-6">
            <h2 className="text-[24px] font-bold text-left">{title}</h2>
            <p className="text-[20px] mt-1 text-left">
              <span>{displayPrice || price}</span>
              {displayCompareAt && displayCompareAt !== (displayPrice || price) && (
                <span className="text-[12px] text-gray-500 line-through ml-2 align-middle">{displayCompareAt}</span>
              )}
            </p>
          </div>

          {dims.map((dimName, i) => {
            const options = getOptionsForIndex(i, selectedValues.slice(0, i));
            const selected = selectedValues[i];
            return (
              <ProductOptionGroup
                key={dimName}
                name={dimName}
                options={options}
                selected={selected}
                onChange={(val) => handleChangeAt(i, val)}
              />
            );
          })}

          <div className="mt-6">
            <button
              type="button"
              onClick={handleFetchRecommendations}
              disabled={busy}
              className="px-4 py-2 text-sm rounded border bg-white disabled:opacity-50"
            >
              {busy ? "Hämtar…" : "Hämta rekommendationer"}
            </button>
            {error && <div className="mt-2 text-[12px] text-red-700">{error}</div>}
            {!error && lastCount != null && (
              <div className="mt-2 text-[12px] text-gray-600">Stored {lastCount} recommendations.</div>
            )}
          </div>

          <div className="mt-6 rounded-[12px] border p-4">
            <RecommendationsWidget storeId={storeId} apiBase="" />
          </div>

          <div
            className="mt-6 text-gray-700 text-left"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          />
        </div>
      </div>
    </div>
  );
}
